import { useMemo } from 'react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

type PortalTab = {
  value: string
  label: string
  disabled: boolean
}

const PORTAL_TABS: readonly PortalTab[] = [
  { value: 'overview', label: 'Overview', disabled: false },
  { value: 'financials', label: 'Financials', disabled: false },
  { value: 'ogsm', label: 'OGSM', disabled: false },
  { value: 'management', label: 'Management', disabled: true },
  { value: 'transactions', label: 'Transactions', disabled: true },
  { value: 'cap-table', label: 'Cap Table', disabled: true },
  { value: 'general', label: 'General', disabled: true },
] as const

export type PortalTabValue = (typeof PORTAL_TABS)[number]['value']

export function isPortalTabValue(value: string): value is PortalTabValue {
  return PORTAL_TABS.some((tab) => tab.value === value)
}

export function PortalTabs({
  value,
  onValueChange,
  showTeam = false,
  showOgsm = false,
}: {
  value: PortalTabValue
  onValueChange: (value: PortalTabValue) => void
  showTeam?: boolean
  /** Flag and `ogsm:view:own`; without them the tab is left out, not disabled. */
  showOgsm?: boolean
}) {
  // Portco admins get access to the Management surface (team members).
  // Other users see Management as a disabled placeholder.
  // Scorecard is internal-only, regardless of the rollout flag.
  const tabs = useMemo<readonly PortalTab[]>(
    () =>
      PORTAL_TABS.flatMap((tab) => {
        if (tab.value === 'ogsm' && !showOgsm) return []
        if (tab.value === 'management' && showTeam) {
          return [{ ...tab, disabled: false }]
        }
        return [tab]
      }),
    [showTeam, showOgsm]
  )

  return (
    <Tabs
      value={value}
      onValueChange={(next) => {
        if (isPortalTabValue(next)) onValueChange(next)
      }}
      className="w-fit max-w-full"
    >
      <TabsList className="inline-flex h-auto max-w-full flex-wrap justify-start gap-1 rounded-md bg-secondary/8 p-1">
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.value}
            id={`portal-tab-${tab.value}`}
            aria-controls="portal-tabpanel"
            value={tab.value}
            disabled={tab.disabled}
            className="h-auto text-xs sm:text-sm data-[state=active]:px-2 data-[state=active]:py-1"
          >
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}
